/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface LocationHazardProps {
  id: string;
  locationId: string;
  hazard: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class LocationHazardRecord extends Record<LocationHazardProps>({
  id: null as any,
  locationId: null as any,
  hazard: null as any,
  effectiveDate: null as any,
  expirationDate: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    locationId: string;
    hazard: string;
    effectiveDate: string | number | Date | null;
    expirationDate: string | number | Date | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): LocationHazardRecord {
    if (json == null) {
      throw new Error('LocationHazardRecord.fromJS: parameter cannot be null');
    }
    return new LocationHazardRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'LocationHazardJson.id',
      ),
      locationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'locationId', '00000000-0000-0000-0000-000000000000'),
        'LocationHazardJson.locationId',
      ),
      hazard: getFromOrThrow(json, (j) => j.hazard, 'LocationHazardJson.hazard'),
      effectiveDate: json.effectiveDate != null ? new Date(json.effectiveDate) : null,
      expirationDate: json.expirationDate != null ? new Date(json.expirationDate) : null,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'LocationHazardJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    locationId: string;
    hazard: string;
    effectiveDate: string | number | Date | null;
    expirationDate: string | number | Date | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [LocationHazardRecord, Error[]] {
    if (json == null) {
      throw new Error('LocationHazardRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new LocationHazardRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'LocationHazardJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'locationId', '00000000-0000-0000-0000-000000000000'),
            'LocationHazardJson.locationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      hazard: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.hazard, 'LocationHazardJson.hazard'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      effectiveDate: tryOrDefault(
        () => (json.effectiveDate != null ? new Date(json.effectiveDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      expirationDate: tryOrDefault(
        () => (json.expirationDate != null ? new Date(json.expirationDate) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'LocationHazardJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: LocationHazardProps) {
    super(props);
    if (props == null) {
      throw new Error('LocationHazardRecord constructor parameter cannot be null');
    }
  }
}

export abstract class LocationHazardViewModelBase implements LocationHazardProps {
  protected readonly _props: LocationHazardProps;
  private _json?: LocationHazardProps;

  get id(): string {
    return this._props.id;
  }
  get locationId(): string {
    return this._props.locationId;
  }
  get hazard(): string {
    return this._props.hazard;
  }
  get effectiveDate(): Date | null {
    return this._props.effectiveDate;
  }
  get expirationDate(): Date | null {
    return this._props.expirationDate;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: LocationHazardProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        locationId: this._props.locationId,
        hazard: this._props.hazard,
        effectiveDate: this._props.effectiveDate,
        expirationDate: this._props.expirationDate,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllLocationHazardEntities = (state: AppState) =>
  state.entityStores.locationHazard.entities;

export const selectLocationHazardById = (id: string) => (state: AppState) =>
  state.entityStores.locationHazard.entities.get(id);

export const selectLocationHazardMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.locationHazard.meta.get(id);

export const selectLocationHazardAndMetadataById = (id: string) =>
  createSelector(
    selectLocationHazardById(id),
    selectLocationHazardMetadataById(id),
    (locationHazard, meta) => ({ locationHazard, meta }),
  );

export function selectLocationHazardEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllLocationHazardEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, LocationHazardRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectLocationHazardEntitiesByLocationId(locationId: string) {
  return createSelector(selectAllLocationHazardEntities, (entities) =>
    entities.filter((e) => e.locationId === locationId).valueSeq(),
  );
}

export function selectLocationHazardEntitiesByHazard(hazard: string) {
  return createSelector(selectAllLocationHazardEntities, (entities) =>
    entities.filter((e) => e.hazard === hazard).valueSeq(),
  );
}

export function selectLocationHazardEntitiesByEffectiveDate(effectiveDate: Date) {
  return createSelector(selectAllLocationHazardEntities, (entities) =>
    entities.filter((e) => e.effectiveDate === effectiveDate).valueSeq(),
  );
}

export function selectLocationHazardEntitiesByExpirationDate(expirationDate: Date) {
  return createSelector(selectAllLocationHazardEntities, (entities) =>
    entities.filter((e) => e.expirationDate === expirationDate).valueSeq(),
  );
}

export function selectLocationHazardEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllLocationHazardEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
